import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TableBox from '../../../../components/TableBox';
import StatusBadge from '../../../../components/StatusBadge';
import RowActions from '../../../../components/RowActions';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import useFetch from '../../../../hooks/useFetch';
import usePagination from '../../../../hooks/usePagination';
import { usePermission } from '../../../../hooks/usePermission';
import InternalUserService from '../../../../services/internal-user.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { SortOrder, Modules, AuthRoutes, RouteParamsTypes, NonAuthRoutes } from '../../../../enums';
import useSorting from '../../../../hooks/useSorting';
import { sortData } from '../../../../utils/sortUtils';

interface InternalUserRow {
  id: number;
  full_name: string | null;
  email: string;
  role_name: string;
  role_scope: 'global' | 'product';
  status: 'invited' | 'active' | 'deactivated';
  product_scope: number[];
  last_login: string | null;
  has_audit_history: boolean;
}

const InternalUsersList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManageUsers = usePermission('user:invite');
  const { loggedInUser } = useContext(AuthContext);

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: 'full_name' });
  const [items, setItems] = useState<InternalUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [deactivateTarget, setDeactivateTarget] = useState<InternalUserRow | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<InternalUserRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const { loading } = useFetch({
    service: InternalUserService,
    func: 'list',
    payload: { page, limit: perPage },
    dependency: [refreshToken, page, perPage],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  const handleResendInvite = useCallback(
    async (id: number) => {
      try {
        await InternalUserService.resendInvite(id);
        toast.success(<Toast message={t('iam.internalUsers.list.pageTexts.resendSuccess')} />, { icon: false });
        setRefreshToken((v) => v + 1);
      } catch (err) {
        // interceptor already surfaces the error toast
      }
    },
    [t]
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`${Modules.iam}${AuthRoutes.internalUsers}${RouteParamsTypes.edit}/${id}`);
    },
    [navigate]
  );

  const handleReactivate = useCallback(
    async (id: number) => {
      try {
        await InternalUserService.reactivate(id);
        toast.success(<Toast message={t('iam.internalUsers.list.pageTexts.reactivateSuccess')} />, { icon: false });
        setRefreshToken((v) => v + 1);
      } catch (err) {
        // interceptor already surfaces the error toast
      }
    },
    [t]
  );

  const closeDeactivateModal = useCallback(() => {
    setDeactivateTarget(null);
    setDeactivateReason('');
  }, []);

  const handleDeactivateConfirm = useCallback(async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await InternalUserService.deactivate(deactivateTarget.id, deactivateReason || undefined);
      toast.success(<Toast message={t('iam.internalUsers.list.pageTexts.deactivateSuccess')} />, { icon: false });
      setRefreshToken((v) => v + 1);
      closeDeactivateModal();
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [deactivateTarget, deactivateReason, closeDeactivateModal, t]);

  const closeDeleteModal = useCallback(() => {
    setDeleteTarget(null);
    setDeleteReason('');
    setDeleteReasonError('');
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    if (!deleteReason.trim()) {
      setDeleteReasonError(t('iam.internalUsers.list.pageTexts.deleteModal.reasonRequired'));
      return;
    }
    setActionLoading(true);
    try {
      await InternalUserService.remove(deleteTarget.id, deleteReason.trim());
      toast.success(<Toast message={t('iam.internalUsers.list.pageTexts.deleteSuccess')} />, { icon: false });
      setRefreshToken((v) => v + 1);
      closeDeleteModal();
    } catch (err) {
      // interceptor already surfaces the error toast
    } finally {
      setActionLoading(false);
    }
  }, [deleteTarget, deleteReason, closeDeleteModal, t]);

  const headCells: HeadCellType[] = useMemo(
    () => [
      { id: 'full_name', sortEnabled: true, width: '18%', label: t('iam.internalUsers.list.pageTexts.table.columns.name') },
      { id: 'email', sortEnabled: true, width: '20%', label: t('iam.internalUsers.list.pageTexts.table.columns.email') },
      { id: 'role_name', sortEnabled: true, width: '13%', label: t('iam.internalUsers.list.pageTexts.table.columns.role') },
      { id: 'status', sortEnabled: true, width: '11%', label: t('iam.internalUsers.list.pageTexts.table.columns.status') },
      { id: 'last_login', sortEnabled: true, width: '13%', label: t('iam.internalUsers.list.pageTexts.table.columns.lastLogin') },
      { id: 'actions', sortEnabled: false, width: '25%', label: t('iam.internalUsers.list.pageTexts.table.columns.actions'), textAlign: 'right' },
    ],
    [t]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: InternalUserRow; className?: string }) => {
      const isSelf = Number(loggedInUser?.id) === item.id;
      return (
        <tr className={className}>
          <td>{item.full_name ?? '—'}</td>
          <td>{item.email}</td>
          <td>{item.role_name}</td>
          <td>
            <StatusBadge
              label={item.status}
              colour={item.status === 'active' ? '#2e7d32' : item.status === 'invited' ? '#ed6c02' : '#757575'}
            />
          </td>
          <td>{item.last_login ? new Date(item.last_login).toLocaleString() : '—'}</td>
          <td className="text-end">
            <RowActions
              actions={[
                item.status === 'invited' && {
                  id: 'resend',
                  label: t('iam.internalUsers.list.pageTexts.resendInvite') || 'Resend Invite',
                  icon: <i className="ri-mail-send-line" />,
                  onClick: () => handleResendInvite(item.id),
                },
                {
                  id: 'edit',
                  label: t('iam.internalUsers.list.pageTexts.edit') || 'Edit',
                  icon: <i className="ri-pencil-line" />,
                  onClick: () => handleEdit(item.id),
                },
                item.status === 'active' && !isSelf && {
                  id: 'deactivate',
                  label: t('iam.internalUsers.list.pageTexts.deactivate') || 'Deactivate',
                  icon: <i className="ri-forbid-line" />,
                  danger: true,
                  onClick: () => setDeactivateTarget(item),
                },
                item.status === 'deactivated' && {
                  id: 'reactivate',
                  label: t('iam.internalUsers.list.pageTexts.reactivate') || 'Reactivate',
                  icon: <i className="ri-restart-line" />,
                  onClick: () => handleReactivate(item.id),
                },
                !isSelf && {
                  id: 'delete',
                  label: t('iam.internalUsers.list.pageTexts.delete') || 'Delete',
                  icon: <i className="ri-delete-bin-line" />,
                  danger: true,
                  onClick: () => setDeleteTarget(item),
                },
              ]}
            />
          </td>
        </tr>
      );
    },
    [handleResendInvite, handleEdit, handleReactivate, loggedInUser, t]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('iam.internalUsers.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, setPage, setPerPage, setOrder, setOrderBy, t]
  );

  if (!canManageUsers) {
    return <Navigate replace to={{ pathname: Modules.error + NonAuthRoutes.forbidden }} />;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('iam.internalUsers.list.pageTexts.title')}</h4>
        <Button
          className="btn btn-primary"
          text={t('iam.internalUsers.list.pageTexts.addNew')}
          onClick={() => navigate(`${Modules.iam}${AuthRoutes.internalUsers}${RouteParamsTypes.add}`)}
        />
      </div>

      <TableBox tableValues={values} />

      <Modal
        open={!!deactivateTarget}
        onClose={closeDeactivateModal}
        title={t('iam.internalUsers.list.pageTexts.deactivateModal.title')}
        content={
          <>
            <p>
              {t('iam.internalUsers.list.pageTexts.deactivateModal.message', {
                name: deactivateTarget?.full_name ?? deactivateTarget?.email,
              })}
            </p>
            <label className="form-label d-block" htmlFor="deactivate-reason">
              {t('iam.internalUsers.list.pageTexts.deactivateModal.reasonLabel')}
            </label>
            <textarea
              id="deactivate-reason"
              className="form-control"
              rows={2}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
            />
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeDeactivateModal} />
            <Button
              className="btn btn-danger"
              text={t('iam.internalUsers.list.pageTexts.deactivateModal.confirm')}
              onClick={handleDeactivateConfirm}
              loading={actionLoading}
            />
          </>
        }
      />

      <Modal
        open={!!deleteTarget}
        onClose={closeDeleteModal}
        title={t('iam.internalUsers.list.pageTexts.deleteModal.title')}
        content={
          <>
            <p>
              {deleteTarget?.has_audit_history
                ? t('iam.internalUsers.list.pageTexts.deleteModal.softMessage', {
                    name: deleteTarget?.full_name ?? deleteTarget?.email,
                  })
                : t('iam.internalUsers.list.pageTexts.deleteModal.hardMessage', {
                    name: deleteTarget?.full_name ?? deleteTarget?.email,
                  })}
            </p>
            <label className="form-label d-block" htmlFor="delete-reason">
              {t('iam.internalUsers.list.pageTexts.deleteModal.reasonLabel')}
            </label>
            <textarea
              id="delete-reason"
              className={`form-control ${deleteReasonError ? 'is-invalid' : ''}`}
              rows={2}
              value={deleteReason}
              onChange={(e) => {
                setDeleteReason(e.target.value);
                if (deleteReasonError) setDeleteReasonError('');
              }}
            />
            {deleteReasonError && <div className="text-danger mt-1 small">{deleteReasonError}</div>}
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('common.buttons.cancel')} onClick={closeDeleteModal} />
            <Button
              className="btn btn-danger"
              text={t('iam.internalUsers.list.pageTexts.deleteModal.confirm')}
              onClick={handleDeleteConfirm}
              loading={actionLoading}
            />
          </>
        }
      />
    </>
  );
};

export default InternalUsersList;
